import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Document,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_SUBTYPE_LABELS,
  type DocumentSubType,
} from '@/types/document';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { Download, ExternalLink, FileText, Calendar } from 'lucide-react';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

interface DocumentViewerProps {
  document: Document | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileUrl?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  open,
  onOpenChange,
  fileUrl
}) => {
  if (!document) return null;

  const isImageFile = (url?: string) => {
    if (!url) return false;
    // Extract filename from URL (before query parameters)
    const filename = url.split('?')[0];
    return /\.(jpg|jpeg|png|gif|webp|bmp|tiff|svg)$/i.test(filename);
  };

  const isPdfFile = (url?: string) => {
    if (!url) return false;
    // Extract filename from URL (before query parameters)
    const filename = url.split('?')[0];
    return /\.pdf$/i.test(filename);
  };

  const isDocumentFile = (url?: string) => {
    if (!url) return false;
    // Extract filename from URL (before query parameters)
    const filename = url.split('?')[0];
    return /\.(doc|docx|xls|xlsx|ppt|pptx|txt|rtf)$/i.test(filename);
  };

  const getFileType = (url?: string) => {
    if (!url) return 'unknown';
    // Extract filename from URL (before query parameters)
    const filename = url.split('?')[0];
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension || 'unknown';
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <span>
              {document.documentType === 'OTHER' && document.description?.trim()
                ? document.description.trim()
                : DOCUMENT_TYPE_LABELS[document.documentType]}
            </span>
            {(document.subTypeLabel ??
              (document.subType
                ? DOCUMENT_SUBTYPE_LABELS[document.subType as DocumentSubType] ??
                  null
                : null)) && (
              <span className="text-sm font-normal text-muted-foreground">
                ·{' '}
                {document.subTypeLabel ??
                  (document.subType
                    ? DOCUMENT_SUBTYPE_LABELS[
                        document.subType as DocumentSubType
                      ] ?? document.subType
                    : '')}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Document details and preview
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[70vh]">
          {/* Document Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Status:</span>
                <DocumentStatusBadge status={document.status} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Type:</span>
                <Badge variant="outline" className="text-xs">
                  {document.isRequired ? 'Required' : 'Optional'}
                </Badge>
              </div>
              {document.documentType === 'OTHER' && document.description?.trim() && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">What it&apos;s for:</span>{' '}
                  {document.description.trim()}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Uploaded: {formatAdminBusinessEt(document.uploadedAt)}</span>
              </div>
              {document.approvedAt && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Approved: {formatAdminBusinessEt(document.approvedAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Reason */}
          {document.status === 'REJECTED' && document.rejectionReason && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2">Rejection Reason</h4>
              <p className="text-sm text-red-700">{document.rejectionReason}</p>
            </div>
          )}

          {/* Notes */}
          {document.notes && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Notes</h4>
              <p className="text-sm text-blue-700">{document.notes}</p>
            </div>
          )}

          {/* File Preview */}
          {fileUrl ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Document Preview</h4>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(fileUrl, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in New Tab
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        const link = window.document.createElement('a');
                        link.href = fileUrl;
                        link.download = `${DOCUMENT_TYPE_LABELS[document.documentType]}.${fileUrl.split('.').pop()}`;
                        link.click();
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              </div>


              <div className="border rounded-lg overflow-hidden">
                {isImageFile(fileUrl) ? (
                  <img
                    src={fileUrl}
                    alt={DOCUMENT_TYPE_LABELS[document.documentType]}
                    className="w-full h-auto max-h-96 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : isPdfFile(fileUrl) ? (
                  <iframe
                    src={fileUrl}
                    className="w-full h-96 border-0"
                    title={DOCUMENT_TYPE_LABELS[document.documentType]}
                  />
                ) : isDocumentFile(fileUrl) ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Document preview not available</p>
                    <p className="text-sm">File type: {getFileType(fileUrl).toUpperCase()}</p>
                    <p className="text-sm">Click "Open in New Tab" to view the document</p>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Preview not available for this file type</p>
                    <p className="text-sm">File type: {getFileType(fileUrl).toUpperCase()}</p>
                    <p className="text-sm">Click "Open in New Tab" to view the document</p>
                  </div>
                )}
                
                {/* Fallback for failed image loads */}
                <div className="hidden p-8 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Unable to load preview</p>
                  <p className="text-sm">Click "Open in New Tab" to view the document</p>
                </div>
              </div>
            </div>
          ) : null}

          {/* No File Available */}
          {!fileUrl && (
            <div className="p-8 text-center text-muted-foreground border rounded-lg">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>File preview not available</p>
              <p className="text-sm">The document file may not be accessible at this time</p>
              <p className="text-xs mt-2 text-muted-foreground">
                Document status: {document.status}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
